import { getErrorMessage } from '~/utils/errors'

/** Session: el usuario llegó al checkout y salió sin completar compra (recuperación de carrito). */
export const SESSION_CHECKOUT_PAGE_VISITED = 'byhours_checkout_page_visited'

/** Session: fingerprints de `checkout_started` en esta pestaña (anti-bounce). */
export const SESSION_CHECKOUT_STARTED_KEYS = 'byhours_checkout_started_keys'

export const useCheckoutStore = defineStore('checkout', () => {
  // Estado reactivo para los datos del checkout
  const checkoutData = ref<CheckoutData | null>(null)
  
  // Estado para indicar si los datos están listos
  const isDataReady = ref(false)
  
  // Función para establecer los datos del checkout
  const setCheckoutData = (data: CheckoutData) => {
    checkoutData.value = data
    isDataReady.value = true
    
    // Persistir temporalmente en sessionStorage para mantener datos durante navegación
    if (import.meta.client) {
      sessionStorage.setItem('checkout_data', JSON.stringify(data))
      // Nueva selección: aún no ha “abandonado” el checkout de esta reserva
      sessionStorage.removeItem('byhours_checkout_recovery_dismissed')
      sessionStorage.removeItem(SESSION_CHECKOUT_PAGE_VISITED)
    }
  }
  
  // Función para obtener los datos del checkout
  const getCheckoutData = (): CheckoutData | null => {
    // Si no hay datos en memoria, intentar recuperar de sessionStorage
    if (!checkoutData.value && import.meta.client) {
      restoreFromSession()
    }

    return checkoutData.value
  }

  const restoreFromSession = (): CheckoutData | null => {
    if (!import.meta.client) return checkoutData.value

    const storedData = sessionStorage.getItem('checkout_data')
    if (!storedData) return checkoutData.value

    try {
      const parsedData = JSON.parse(storedData) as CheckoutData
      checkoutData.value = parsedData
      isDataReady.value = true
      return parsedData
    } catch (error: unknown) {
      console.error('Error parsing stored checkout data:', getErrorMessage(error))
      clearCheckoutData()
      return null
    }
  }
  
  // Función para limpiar los datos del checkout
  const clearCheckoutData = () => {
    checkoutData.value = null
    isDataReady.value = false
    
    if (import.meta.client) {
      sessionStorage.removeItem('checkout_data')
      sessionStorage.removeItem(SESSION_CHECKOUT_PAGE_VISITED)
      sessionStorage.removeItem(SESSION_CHECKOUT_STARTED_KEYS)
    }
  }
  
  // Función para verificar si hay datos válidos
  const hasValidData = (): boolean => {
    const data = getCheckoutData()
    if (data === null || data.totalAmount == null) return false
    const hotelId = data.hotelId ?? data.hotelData?.id
    const roomId = data.roomId ?? data.roomData?.id
    return !!(hotelId && roomId)
  }
  
  // Función para actualizar un campo específico
  const updateCheckoutField = <K extends keyof CheckoutData>(field: K, value: CheckoutData[K]) => {
    if (checkoutData.value) {
      checkoutData.value[field] = value
      
      // Actualizar también en sessionStorage
      if (import.meta.client) {
        sessionStorage.setItem('checkout_data', JSON.stringify(checkoutData.value))
      }
    }
  }
  
  return {
    checkoutData,
    isDataReady,
    setCheckoutData,
    getCheckoutData,
    restoreFromSession,
    clearCheckoutData,
    hasValidData,
    updateCheckoutField
  }
}) 